import { Box, MenuItem, Select, Typography } from '@mui/material';
import {
  ContentVideoContentModel,
  ResourceCategoryEnum,
} from '@protos/content_management/content.db_pb';
import { useEffect, useMemo, useState } from 'react';
import { pxToRem, pxTovW } from '../../../../commonUtils/resizeUtils';
import { IStyles } from '../../../../commonUtils/styleUtils';
import {
  FeatureGateName,
  useFeatureGate,
} from '../../../../commonUtils/featureSchoolGate';
import ElementRenderer from '../../ElementRenderer';
import {
  SelectedResourceInfo,
  VideoPlayerWrapper,
} from '../../VideoPlayerWrapper/VideoPlayerWrapper';

/** The default video has no displayName of its own; every published one is English. */
const DEFAULT_LANGUAGE_LABEL = 'English';

const styles: IStyles = {
  videoWrapper: {
    gap: {
      xs: pxToRem(15),
      md: pxTovW(20),
    },
    display: 'flex',
    flexDirection: 'column',
  },
  languageRow: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(10), md: pxTovW(13) },
    marginBottom: { xs: pxToRem(10), md: pxTovW(12) },
  },
  languageSelect: {
    background: '#F5FAFF',
    borderRadius: { xs: pxToRem(10), md: pxTovW(10) },
    height: { xs: pxToRem(40), md: pxTovW(50) },
    minWidth: { xs: pxToRem(130), md: pxTovW(180) },
    '.MuiOutlinedInput-notchedOutline': { borderColor: '#007CDC' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#007CDC' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#007CDC',
    },
    boxShadow: 'none',
  },
};

interface IProps {
  contentVideo: ContentVideoContentModel;
  selected_resource_info?: SelectedResourceInfo;
  resourceCategory?: ResourceCategoryEnum;
  disableDownload?: boolean;
  allowUserControls?: boolean;
  openPdfInNewTab?: boolean;
  /** Gated by the GIL feature flag - when false only the default language plays. */
  isMultiLanguageEnabled?: boolean;
}
export const ContentVideoContent = (props: IProps) => {
  const {
    contentVideo,
    selected_resource_info,
    resourceCategory,
    disableDownload,
    allowUserControls,
    openPdfInNewTab,
    isMultiLanguageEnabled = false,
  } = props;

  const elements = contentVideo.elements;
  const annotationsEnabled = useFeatureGate(FeatureGateName.annotationsEnabled);

  // The default language plays from the video element itself, so it needs no
  // override url. localizedVideos holds the extras; one without a displayName
  // has nothing to name it with, so it is left out.
  const languageOptions = useMemo(
    () => [
      {
        code: contentVideo.languageCode || 'en',
        label: DEFAULT_LANGUAGE_LABEL,
        url: undefined as string | undefined,
      },
      ...(isMultiLanguageEnabled
        ? contentVideo.localizedVideos ?? []
        : []
      ).flatMap((video) =>
        video.displayName
          ? [
              {
                code: video.languageCode,
                // Already in its own language - rendered as is.
                label: video.displayName,
                url: video.primaryVideoUrl,
              },
            ]
          : []
      ),
    ],
    [contentVideo, isMultiLanguageEnabled]
  );

  const [selectedCode, setSelectedCode] = useState(languageOptions[0].code);

  // A different resource starts back on its own default language.
  useEffect(() => {
    setSelectedCode(languageOptions[0].code);
  }, [languageOptions]);

  const selectedOption =
    languageOptions.find((option) => option.code === selectedCode) ||
    languageOptions[0];

  return (
    <Box sx={styles.videoWrapper}>
      <Box>
        {languageOptions.length > 1 && (
          <Box sx={styles.languageRow}>
            <Typography variant="h4" fontWeight="bold">
              Language
            </Typography>
            <Select
              value={selectedOption.code}
              onChange={(event) => setSelectedCode(event.target.value)}
              sx={styles.languageSelect}
            >
              {languageOptions.map((option) => (
                <MenuItem key={option.code} value={option.code}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}
        <VideoPlayerWrapper
          videoUrl={contentVideo.primaryVideoUrl}
          selected_resource_info={selected_resource_info}
          resourceCategory={resourceCategory}
          allowUserControls={allowUserControls}
          annotationsEnabled={annotationsEnabled}
        />
      </Box>
      <ElementRenderer
        elements={elements}
        selected_resource_info={selected_resource_info}
        resourceCategory={resourceCategory}
        disableDownload={disableDownload}
        allowUserControls={allowUserControls}
        openPdfInNewTab={openPdfInNewTab}
        videoUrlOverride={selectedOption.url}
      />
    </Box>
  );
};
